import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import * as actions from "../../store/actions/index";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import EmptyModal from "../../components/modals/EmptyModal";
import Button from "../../components/ui/Button";
import FormSectionLoadingSkeleton from "../../components/operational/FormSectionLoadingSkeleton";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";
import { CreditReturnDetailsViewModel } from "../../viewmodels/shipping/CreditReturnsViewModel";
import { withRouter } from "../../shared/utility";

const itemsTableSkeletonColumns = [
  "Barcode",
  "Product",
  "Lot",
  "Disposition",
  "Credit",
];

function CreditReturnDetails(props) {
  const { logout, addSnackbar } = props;
  const { creditReturnId } = useParams();
  const isNew = creditReturnId === "new";
  const [barcode, setBarcode] = useState("");
  const [header, setHeader] = useState({ shipping_id: "", return_type: "physical_return", credit_type: "no_credit", notes: "" });
  const [scanOptions, setScanOptions] = useState({ disposition: "qa_hold", return_reason: "", return_condition: "sealed_original_label", credit_required: false });
  const { creditReturn, loading, emptyModal, setEmptyModelOpen, loadCreditReturn, createCreditReturn, scanReturnItem, postCreditReturn } = CreditReturnDetailsViewModel(creditReturnId, logout, addSnackbar);

  useEffect(() => {
    if (!isNew) loadCreditReturn();
  }, [creditReturnId]);

  const createReturn = () => createCreditReturn(header);
  const scanItem = async () => {
    if (!barcode) return;
    await scanReturnItem(barcode, scanOptions);
    setBarcode("");
  };

  if (isNew) {
    return (
      <div className="qatrace-screen qatrace-module-page">
        <div className="space-y-5 px-4 sm:px-6 lg:px-8">
          <h1 className="text-xl font-semibold leading-6 text-gray-900">Create Return / Credit</h1>
          <div className="card-custom p-4 space-y-4">
            <input className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm" placeholder="Shipment ID" value={header.shipping_id} onChange={(e) => setHeader({ ...header, shipping_id: e.target.value })} />
            <select className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 sm:text-sm" value={header.return_type} onChange={(e) => setHeader({ ...header, return_type: e.target.value })}>
              <option value="physical_return">Physical Return</option>
              <option value="credit_only">Credit Only</option>
              <option value="replacement">Replacement</option>
            </select>
            <select className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 sm:text-sm" value={header.credit_type} onChange={(e) => setHeader({ ...header, credit_type: e.target.value })}>
              <option value="no_credit">No Credit</option>
              <option value="full_credit">Full Credit</option>
              <option value="partial_credit">Partial Credit</option>
              <option value="replacement_only">Replacement Only</option>
            </select>
            <textarea className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm" placeholder="Notes" value={header.notes} onChange={(e) => setHeader({ ...header, notes: e.target.value })} />
            <Button type="button" variant="primary" size="md" onClick={createReturn}>Create Return</Button>
          </div>
        </div>
        <EmptyModal childComponent={emptyModal.childComponent} open={emptyModal.open} setOpen={setEmptyModelOpen} />
      </div>
    );
  }

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div className="space-y-5 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {loading && !creditReturn ? (
            <FormSectionLoadingSkeleton rows={2} className="max-w-md" />
          ) : (
            <div>
              <h1 className="text-xl font-semibold leading-6 text-gray-900">Return / Credit {creditReturn?.credit_return_number}</h1>
              <p className="mt-1 text-sm text-gray-600">{creditReturn?.customer_name}</p>
            </div>
          )}
          {creditReturn ? (
            <Button type="button" variant="primary" size="md" onClick={postCreditReturn}>Post Credit Return</Button>
          ) : null}
        </div>

        {creditReturn ? (
          <>
            <div className="card-custom p-4 space-y-3">
              <label className="text-sm block font-medium text-gray-900">Scan returned barcode</label>
              <input className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm" placeholder="Scan returned barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scanItem()} />
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <input className="border border-gray-300 rounded-md py-2 px-3 sm:text-sm" placeholder="Reason" value={scanOptions.return_reason} onChange={(e) => setScanOptions({ ...scanOptions, return_reason: e.target.value })} />
                <select className="border border-gray-300 rounded-md py-2 px-3 sm:text-sm" value={scanOptions.disposition} onChange={(e) => setScanOptions({ ...scanOptions, disposition: e.target.value })}>
                  <option value="qa_hold">QA Hold</option>
                  <option value="return_to_stock">Return to Stock</option>
                  <option value="dispose">Dispose</option>
                  <option value="credit_only">Credit Only</option>
                </select>
                <select className="border border-gray-300 rounded-md py-2 px-3 sm:text-sm" value={scanOptions.return_condition} onChange={(e) => setScanOptions({ ...scanOptions, return_condition: e.target.value })}>
                  <option value="sealed_original_label">Sealed / Original Label</option>
                  <option value="opened">Opened</option>
                  <option value="damaged_packaging">Damaged Packaging</option>
                  <option value="missing_label">Missing Label</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={scanOptions.credit_required} onChange={(e) => setScanOptions({ ...scanOptions, credit_required: e.target.checked })} /> Credit required</label>
              </div>
              <Button type="button" variant="secondary" size="md" onClick={scanItem}>Add Returned Item</Button>
            </div>

            <div className="card-custom p-3">
              <DataTable value={creditReturn?.items ?? []} dataKey="id" className="p-datatable-sm text-sm" emptyMessage="No returned items yet.">
                <Column field="barcode" header="Barcode" />
                <Column field="product_name" header="Product" />
                <Column field="lot_number" header="Lot" />
                <Column field="disposition" header="Disposition" />
                <Column field="credit_status" header="Credit" />
              </DataTable>
            </div>
          </>
        ) : loading ? (
          <div className="space-y-5">
            <div className="card-custom p-4">
              <FormSectionLoadingSkeleton rows={4} />
            </div>
            <div className="card-custom p-3">
              <OperationalDataTableSkeleton
                columns={itemsTableSkeletonColumns}
                rows={5}
              />
            </div>
          </div>
        ) : null}
      </div>
      <EmptyModal childComponent={emptyModal.childComponent} open={emptyModal.open} setOpen={setEmptyModelOpen} />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => ({ logout: () => dispatch(actions.logout()), addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)) });
export default withRouter(connect(null, mapDispatchToProps)(CreditReturnDetails));
